import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Trash2, CheckCircle, UserMinus, MapPin, Clock, Lightbulb, Send, Loader2, MessageSquare, UserPlus, ShoppingBag, Thermometer, AlertTriangle, Package, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [suggestionDetails, setSuggestionDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replying, setReplying] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superadmin/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Alle Benachrichtigungen anzeigen – kein Typ-Filter mehr
      setNotifications(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Fehler beim Laden der Benachrichtigungen');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read first
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // If it's a forwarded suggestion notification, show details in the notification itself
    // The details are already in the message
  };

  const fetchSuggestionDetails = async (suggestionId) => {
    setLoadingDetails(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/improvement-suggestions/${suggestionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuggestionDetails(response.data);
      setNewStatus(response.data.status || 'pending');
      setSelectedSuggestion(response.data);
    } catch (error) {
      console.error('Failed to fetch suggestion:', error);
      toast.error('Fehler beim Laden des Vorschlags');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim() || !selectedSuggestion) return;

    setReplying(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/improvement-suggestions/${selectedSuggestion.id}/reply`, {
        message: replyMessage.trim()
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Antwort gesendet');
      setReplyMessage('');
      
      // Refresh details
      await fetchSuggestionDetails(selectedSuggestion.id);
    } catch (error) {
      console.error('Failed to reply:', error);
      toast.error('Fehler beim Senden');
    } finally {
      setReplying(false);
    }
  };

  const handleStatusChange = async () => {
    if (!selectedSuggestion) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/improvement-suggestions/${selectedSuggestion.id}/status`, null, {
        params: { status: newStatus },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Status aktualisiert');
      
      // Refresh details
      await fetchSuggestionDetails(selectedSuggestion.id);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleArchive = async () => {
    if (!selectedSuggestion) return;

    setArchiving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/improvement-suggestions/${selectedSuggestion.id}/archive`, null, {
        params: { reason: archiveReason || null },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Vorschlag archiviert');
      setShowArchiveDialog(false);
      setArchiveReason('');
      setSelectedSuggestion(null);
      setSuggestionDetails(null);
      
      // Refresh notifications
      fetchNotifications();
    } catch (error) {
      console.error('Failed to archive:', error);
      toast.error('Fehler beim Archivieren');
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchive = async () => {
    if (!selectedSuggestion) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/improvement-suggestions/${selectedSuggestion.id}/unarchive`, null, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Vorschlag wiederhergestellt');
      
      // Refresh details
      await fetchSuggestionDetails(selectedSuggestion.id);
    } catch (error) {
      console.error('Failed to unarchive:', error);
      toast.error('Fehler beim Wiederherstellen');
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/superadmin/notifications/${notificationId}/read`, null, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      toast.error('Fehler beim Markieren');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/superadmin/notifications/${notificationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Benachrichtigung gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await Promise.all(
        notifications.filter(n => !n.is_read).map(n => 
          axios.put(`${API}/superadmin/notifications/${n.id}/read`, null, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        )
      );
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('Alle als gelesen markiert');
    } catch (error) {
      toast.error('Fehler beim Markieren');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'new_customer':
        return <UserPlus className="h-5 w-5 text-green-500" />;
      case 'subscription_created':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'subscription_cancelled':
        return <UserMinus className="h-5 w-5 text-red-500" />;
      case 'payment_failed':
        return <Clock className="h-5 w-5 text-red-500" />;
      case 'affiliate_payout':
        return <Send className="h-5 w-5 text-blue-500" />;
      case 'forwarded_suggestion':
      case 'improvement_suggestion':
        return <Lightbulb className="h-5 w-5 text-yellow-500" />;
      case 'new_pending_product':
        return <Package className="h-5 w-5 text-orange-500" />;
      case 'instruction_assigned':
      case 'new_instruction':
        return <Star className="h-5 w-5 text-purple-500" />;
      case 'lotto_low_stock':
        return <ShoppingBag className="h-5 w-5 text-amber-500" />;
      case 'temperature_alarm':
        return <Thermometer className="h-5 w-5 text-red-500" />;
      case 'mhd_alert':
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      default:
        return <Bell className="h-5 w-5 text-blue-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'Ausstehend', color: 'bg-yellow-100 text-yellow-800' },
      reviewed: { text: 'Geprüft', color: 'bg-blue-100 text-blue-800' },
      forwarded: { text: 'Weitergeleitet', color: 'bg-purple-100 text-purple-800' },
      implemented: { text: 'Umgesetzt', color: 'bg-green-100 text-green-800' },
      rejected: { text: 'Abgelehnt', color: 'bg-red-100 text-red-800' }
    };
    return badges[status] || badges.pending;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Bell className="h-6 w-6 sm:h-8 sm:w-8" />
              Benachrichtigungen
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {unreadCount} ungelesene Benachrichtigungen
            </p>
          </div>

          {unreadCount > 0 && (
            <Button onClick={markAllAsRead} variant="outline">
              <CheckCircle className="h-4 w-4 mr-2" />
              Alle als gelesen markieren
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <div className="space-y-3">
          {notifications.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Keine Benachrichtigungen</p>
              </CardContent>
            </Card>
          ) : (
            notifications.map((notification) => (
              <Card 
                key={notification.id} 
                className={`transition-all hover:shadow-md ${
                  !notification.is_read ? 'border-l-4 border-l-blue-500 bg-blue-50' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getTypeIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{notification.title}</h3>
                        {!notification.is_read && (
                          <Badge variant="default" className="bg-blue-500">Neu</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{notification.message}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Clock className="h-3 w-3" />
                        {formatDate(notification.created_at)}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!notification.is_read && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Suggestion Detail Dialog */}
        <Dialog open={!!selectedSuggestion} onOpenChange={() => {
          setSelectedSuggestion(null);
          setSuggestionDetails(null);
          setReplyMessage('');
        }}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-yellow-500" />
                Verbesserungsvorschlag
              </DialogTitle>
            </DialogHeader>

            {loadingDetails ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : suggestionDetails && (
              <div className="space-y-4 py-4">
                {/* Title */}
                <div>
                  <Label className="text-xs text-gray-500">Titel</Label>
                  <h3 className="text-lg font-semibold">{suggestionDetails.title}</h3>
                </div>

                {/* Submitter Info */}
                <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <Label className="text-xs text-gray-500">Eingereicht von</Label>
                    <p className="text-sm font-medium">{suggestionDetails.submitted_by_name}</p>
                    <p className="text-xs text-gray-500">({suggestionDetails.submitted_by_role})</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Standort</Label>
                    <p className="text-sm font-medium">📍 {suggestionDetails.location_name}</p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <Label className="text-xs text-gray-500">Beschreibung</Label>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{suggestionDetails.description}</p>
                </div>

                {/* Photos */}
                {suggestionDetails.photos && suggestionDetails.photos.length > 0 && (
                  <div>
                    <Label className="text-xs text-gray-500">Fotos</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {suggestionDetails.photos.map((photo, idx) => (
                        <img
                          key={idx}
                          src={photo}
                          alt={`Foto ${idx + 1}`}
                          className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-75"
                          onClick={() => window.open(photo, '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Admin Comment */}
                {suggestionDetails.admin_comment && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <Label className="text-xs text-blue-900">Admin-Kommentar</Label>
                    <p className="text-sm text-blue-800 mt-1">{suggestionDetails.admin_comment}</p>
                  </div>
                )}

                {/* Status */}
                <div>
                  <Label>Status ändern</Label>
                  <div className="flex gap-2 items-center mt-2">
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-lg"
                    >
                      <option value="pending">Ausstehend</option>
                      <option value="reviewed">Geprüft</option>
                      <option value="implemented">Umgesetzt</option>
                      <option value="rejected">Abgelehnt</option>
                    </select>
                    <Button onClick={handleStatusChange} size="sm">
                      Speichern
                    </Button>
                  </div>
                </div>

                {/* Replies */}
                {suggestionDetails.replies && suggestionDetails.replies.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Antworten ({suggestionDetails.replies.length})
                    </Label>
                    {suggestionDetails.replies.map((reply) => (
                      <div key={reply.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {reply.user_name} • {new Date(reply.created_at).toLocaleString('de-DE')}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{reply.message}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply Form */}
                <div className="space-y-2 pt-4 border-t">
                  <Label>Ihre Antwort</Label>
                  <Textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Schreiben Sie eine Antwort..."
                    rows={3}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedSuggestion(null);
                  setSuggestionDetails(null);
                  setReplyMessage('');
                }}
              >
                Schließen
              </Button>
              
              {/* Archive/Unarchive Button */}
              {suggestionDetails && (
                suggestionDetails.archived ? (
                  <Button
                    onClick={handleUnarchive}
                    variant="outline"
                    className="bg-green-50 hover:bg-green-100 text-green-700"
                  >
                    Wiederherstellen
                  </Button>
                ) : (
                  <Button
                    onClick={() => setShowArchiveDialog(true)}
                    variant="outline"
                    className="bg-gray-50 dark:bg-gray-800 hover:bg-gray-100"
                  >
                    Archivieren
                  </Button>
                )
              )}
              
              <Button
                onClick={handleReply}
                disabled={replying || !replyMessage.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {replying ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Antworten
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Archive Confirmation Dialog */}
        <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Vorschlag archivieren?</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Der Vorschlag wird für Sie archiviert, bleibt aber für den Ersteller sichtbar.
              </p>
              <div className="space-y-2">
                <Label>Grund (optional)</Label>
                <Textarea
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  placeholder="z.B. Bereits umgesetzt, Nicht umsetzbar, etc."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowArchiveDialog(false);
                setArchiveReason('');
              }}>
                Abbrechen
              </Button>
              <Button
                onClick={handleArchive}
                disabled={archiving}
                className="bg-gray-600 hover:bg-gray-700"
              >
                {archiving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Archivieren'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};